/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import type { UserStory, Epic } from '../types';
import { AcceptanceCriterion } from '@shared/types/user-stories';
import { parseUserStoriesFromMarkdown } from '../utils/markdownParser';
import { FileText, Plus, Search, FilterX, Loader, Download, Upload } from 'lucide-react';
import { SkillShopService } from '../services/skillShopService';
import { UserStoryCard } from '../components/user-stories/UserStoryCard';
import { UserStoryFilters } from '../components/user-stories/UserStoryFilters';
import { UserStoryListHeader } from '../components/user-stories/UserStoryListHeader';
import { UserStoryForm } from '../components/user-stories/UserStoryForm';
import { EpicCard } from '../components/user-stories/EpicCard';
import { RequestInput } from '../components/common/RequestInput';
import { useRequestProcessor } from '../hooks/useRequestProcessor';
import { useApp } from '../contexts/AppContext';
import { getRepoContext } from '../utils/repoContext';
import { useToast } from '../hooks/use-toast';
import { JiraUserStoryImportDialog } from '../components/user-stories/JiraUserStoryImportDialog';
import { LinearUserStoryImportDialog } from '../components/user-stories/LinearUserStoryImportDialog';
import { CsvUserStoryImportDialog } from '../components/user-stories/CsvUserStoryImportDialog';
import { JiraUserStoryExportDialog } from '../components/user-stories/JiraUserStoryExportDialog';
import { LinearUserStoryExportDialog } from '../components/user-stories/LinearUserStoryExportDialog';
import { PdfUserStoryExportDialog } from '../components/user-stories/PdfUserStoryExportDialog';
import { DiffReportDialog } from '../components/user-stories/DiffReportDialog';
import { ActiveProductIndicator } from '../components/common/ActiveProductIndicator';
import { getUserStoriesMarkdown, getIssuesMarkdown, type UserStoryRepoContext, type IssueRepoContext } from '../lib/api';
import { listAgentExecutions } from '../lib/api/agent-executions';
import { parseIssuesFromMarkdown } from '@shared/utils/issues';
import type { Issue } from '@shared/types/issues';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export function UserStories() {
  const { activeProduct, activeRepoUrl, isLoadingProduct, isConfigured, isLinearConfigured, quickRequestState, setQuickRequest } = useApp();
  const location = useLocation();
  const [stories, setStories] = useState<UserStory[]>([]);
  const [epics, setEpics] = useState<Epic[]>([]);
  const [releases, setReleases] = useState<Issue[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loadingStories, setLoadingStories] = useState(false);
  const [viewMode, setViewMode] = useState<'epics' | 'stories'>('epics');

  // Import/Export dialog state
  const [showImportMenu, setShowImportMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showJiraImport, setShowJiraImport] = useState(false);
  const [showLinearImport, setShowLinearImport] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [showJiraExport, setShowJiraExport] = useState(false);
  const [showLinearExport, setShowLinearExport] = useState(false);
  const [showPdfExport, setShowPdfExport] = useState(false);
  const [showDiffReport, setShowDiffReport] = useState(false);

  const [showDeleteStoryDialog, setShowDeleteStoryDialog] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState<UserStory | null>(null);

  const [formData, setFormData] = useState<Partial<UserStory>>({
    title: '',
    role: '',
    goal: '',
    benefit: '',
    acceptanceCriteria: [],
    status: 'Not Started',
    section: '',
    epicId: '',
    links: []
  });

  // State for user story grooming
  const [groomingStories, setGroomingStories] = useState<Set<string>>(new Set());
  
  // State for epic grooming
  const [groomingEpics, setGroomingEpics] = useState<Set<string>>(new Set());

  // Expanded story and epic state
  const [expandedStories, setExpandedStories] = useState<Set<string>>(new Set());
  const [expandedEpics, setExpandedEpics] = useState<Set<string>>(new Set());

  // Field editing state for stories
  const [editingStories, setEditingStories] = useState<Set<string>>(new Set());
  const [editedFields, setEditedFields] = useState<Record<string, Partial<UserStory>>>({});
  const [updatingStatusStories, setUpdatingStatusStories] = useState<Set<string>>(new Set());

  // Field editing state for epics
  const [editingEpics, setEditingEpics] = useState<Set<string>>(new Set());
  const [editedEpicFields, setEditedEpicFields] = useState<Record<string, Partial<Epic>>>({});
  const [updatingEpics, setUpdatingEpics] = useState<Set<string>>(new Set());

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sectionFilter, setSectionFilter] = useState<string>('all');
  const [epicFilter, setEpicFilter] = useState<string>('all');

  const repoContext = useMemo(
    () => getRepoContext(activeProduct ?? null, activeRepoUrl),
    [activeProduct, activeRepoUrl],
  );

  // Convert repoContext to UserStoryRepoContext for API calls
  const userStoryRepoContext: UserStoryRepoContext = useMemo(() => ({
    repoUrl: repoContext.repoUrl,
    repoPath: repoContext.repoPath,
  }), [repoContext]);

  const [skillShopService] = useState(() => new SkillShopService());
  const [loadError, setLoadError] = useState<string | null>(null);
  const { toast } = useToast();

  // Use the generic request processor hook with external state for persistence across navigation
  const requestProcessor = useRequestProcessor({
    onProcess: async (request: string) => {
      const result = await skillShopService.processUserStoryRequest(request, activeProduct);
      if (result.success && result.result) {
        requestProcessor.setResponse(result.result);
        await loadStories();
      } else {
        throw new Error(result.error || 'Failed to process user story request');
      }
    },
    onSuccess: () => {
      console.log('User story request processed successfully');
    },
    externalValue: quickRequestState.userStories,
    onExternalValueChange: (value) => setQuickRequest('userStories', value),
  });

  const statusOptions = ['Not Started', 'In Progress', 'Ready For Review', 'In Review', 'Completed', 'Blocked', 'On Hold'];

  // Sync Groom button state from active agent executions (e.g. after navigation)
  const syncActiveExecutionsForStories = useCallback(async () => {
    const res = await listAgentExecutions({ status: 'pending,running', limit: 100 });
    if (!res.success || !res.executions) return;
    const storyIds = new Set(stories.map((s) => s.id));
    const epicIds = new Set(epics.map((e) => e.id));
    const groomingS = new Set<string>();
    const groomingE = new Set<string>();
    for (const exec of res.executions) {
      const name = (exec.agentName ?? '').toLowerCase();
      if (!name.includes('velocity_grooming')) continue;
      const meta = exec.metadata && typeof exec.metadata === 'object' ? exec.metadata as Record<string, unknown> : null;
      if (!meta) continue;
      const storyId = typeof meta.storyId === 'string' ? meta.storyId : null;
      const epicId = typeof meta.epicId === 'string' ? meta.epicId : null;
      if (storyId && storyIds.has(storyId)) groomingS.add(storyId);
      if (epicId && epicIds.has(epicId)) groomingE.add(epicId);
    }
    setGroomingStories(groomingS);
    setGroomingEpics(groomingE);
  }, [stories, epics]);

  useEffect(() => {
    if (stories.length === 0 && epics.length === 0) return;
    syncActiveExecutionsForStories();
  }, [stories.length, epics.length, syncActiveExecutionsForStories]);

  const hasActiveGroomingRuns = groomingStories.size + groomingEpics.size > 0;
  useEffect(() => {
    if (!hasActiveGroomingRuns) return;
    const interval = setInterval(syncActiveExecutionsForStories, 4000);
    return () => clearInterval(interval);
  }, [hasActiveGroomingRuns, syncActiveExecutionsForStories]);

  // Issue repo context for loading releases
  const issueRepoContext: IssueRepoContext = useMemo(() => ({
    repoUrl: repoContext.repoUrl,
    repoPath: repoContext.repoPath,
  }), [repoContext]);

  // Load stories from markdown file
  const loadStories = useCallback(async () => {
    setLoadingStories(true);
    try {
      setLoadError(null);
      const result = await getUserStoriesMarkdown(userStoryRepoContext);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch user stories');
      }
      
      const markdownContent = result.content || '';
      const parsed = parseUserStoriesFromMarkdown(markdownContent);
      setStories(parsed.stories);
      setEpics(parsed.epics);
      
      // Also load releases for acceptance criteria dropdown
      try {
        const issuesResult = await getIssuesMarkdown(issueRepoContext);
        if (issuesResult.success && issuesResult.content) {
          const parsedIssues = parseIssuesFromMarkdown(issuesResult.content);
          const releaseIssues = parsedIssues.filter(issue => issue.type === 'Release');
          setReleases(releaseIssues);
          console.log(`Loaded ${releaseIssues.length} releases for acceptance criteria dropdown`);
        }
      } catch (releaseError) {
        console.warn('Could not load releases:', releaseError);
        setReleases([]);
      }
      
      console.log(`Loaded ${parsed.stories.length} user stories and ${parsed.epics.length} epics from markdown files`);
    } catch (error) {
      console.error('Error loading user stories:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setLoadError(`Error loading user stories: ${errorMessage}. Make sure the product server is running on port 3002.`);
    } finally {
      setLoadingStories(false);
    }
  }, [userStoryRepoContext, issueRepoContext]);

  // Load stories when repo context is ready
  // Only load if we have an active product - don't load default SkillShop data
  useEffect(() => {
    // Skip if a product is currently being loaded
    if (isLoadingProduct) {
      console.log('[UserStories] Waiting for active product to load before fetching stories');
      return;
    }
    
    // Skip if there's no active product - don't load default data
    if (!activeProduct) {
      console.log('[UserStories] No active product - skipping data load');
      setStories([]);
      setEpics([]);
      setReleases([]);
      return;
    }
    
    void loadStories();
  }, [loadStories, isLoadingProduct, activeProduct]);

  // Handle scrolling to story or epic when hash is present in URL
  useEffect(() => {
    if (!location.hash || loadingStories || (stories.length === 0 && epics.length === 0)) {
      return;
    }

    // Extract the ID from the hash (e.g., "#US-001" -> "US-001" or "#ADM-PLT" -> "ADM-PLT")
    const targetId = location.hash.substring(1);
    
    // Check if it's a story ID (e.g., "US-001", "ADM-PLT-001")
    const targetStory = stories.find(story => story.id === targetId);
    
    // Check if it's an epic ID (e.g., "ADM-PLT")
    const targetEpic = epics.find(epic => epic.id === targetId);

    if (targetStory) {
      // Expand the story
      setExpandedStories(prev => new Set(prev).add(targetStory.id));
      
      // If the story belongs to an epic, expand the epic too
      if (targetStory.epicId) {
        setExpandedEpics(prev => new Set(prev).add(targetStory.epicId!));
      }

      // Scroll to the story after a short delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add a highlight effect
          element.style.transition = 'background-color 0.5s';
          element.style.backgroundColor = '#fef3c7';
          setTimeout(() => {
            element.style.backgroundColor = '';
          }, 2000);
        }
      }, 300);
    } else if (targetEpic) {
      // Expand the epic
      setExpandedEpics(prev => new Set(prev).add(targetEpic.id));

      // Scroll to the epic after a short delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add a highlight effect
          element.style.transition = 'background-color 0.5s';
          element.style.backgroundColor = '#fef3c7';
          setTimeout(() => {
            element.style.backgroundColor = '';
          }, 2000);
        }
      }, 300);
    }
  }, [location.hash, loadingStories, stories, epics]);

  // Handle user story grooming
  const handleGroomStory = async (story: UserStory) => {
    setGroomingStories(prev => new Set(prev).add(story.id));
    
    try {
      const result = await skillShopService.groomUserStory(story, activeProduct);
      
      if (result.success && result.result) {
        console.log('User story grooming completed:', result.result);
        
        // Reload stories to see any updates made by the grooming process
        await loadStories();
        
        // Show success message
        toast({
          title: 'User story groomed',
          description: `"${story.id}" has been broken down into issues.`,
        });
      } else {
        console.error('Failed to groom user story:', result.error);
        toast({
          title: 'Failed to groom user story',
          description: result.error || 'The user story could not be groomed.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error grooming user story:', error);
      toast({
        title: 'Failed to groom user story',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setGroomingStories(prev => {
        const newSet = new Set(prev);
        newSet.delete(story.id);
        return newSet;
      });
    }
  };

  // Handle epic grooming
  const handleGroomEpic = async (epic: Epic) => {
    setGroomingEpics(prev => new Set(prev).add(epic.id));
    
    try {
      // Get all stories that belong to this epic
      const epicStories = stories.filter(s => s.epicId === epic.id);
      
      const result = await skillShopService.groomEpic(epic, epicStories, activeProduct);
      
      if (result.success && result.result) {
        console.log('Epic grooming completed:', result.result);
        
        // Reload stories to see any updates made by the grooming process
        await loadStories();
        
        // Show success message
        toast({
          title: 'Epic groomed',
          description: `"${epic.id}" and its ${epicStories.length} stories have been broken down into issues.`,
        });
      } else {
        console.error('Failed to groom epic:', result.error);
        toast({
          title: 'Failed to groom epic',
          description: result.error || 'The epic could not be groomed.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error grooming epic:', error);
      toast({
        title: 'Failed to groom epic',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setGroomingEpics(prev => {
        const newSet = new Set(prev);
        newSet.delete(epic.id);
        return newSet;
      });
    }
  };

  // Handle status update
  const handleStatusUpdate = async (story: UserStory, newStatus: string) => {
    if (story.status === newStatus) {
      return;
    }

    setUpdatingStatusStories(prev => new Set(prev).add(story.id));
    
    try {
      const result = await skillShopService.updateUserStoryStatus(
        story.id, 
        newStatus,
        repoContext.repoUrl,
        repoContext.repoPath
      );
      
      if (result.success) {
        console.log('Status update completed:', result);
        await loadStories();
      } else {
        console.error('Failed to update status:', result.error);
        toast({
          title: 'Failed to update status',
          description: result.error || 'The status could not be updated.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error updating status:', error);
      toast({
        title: 'Failed to update status',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setUpdatingStatusStories(prev => {
        const newSet = new Set(prev);
        newSet.delete(story.id);
        return newSet;
      });
    }
  };

  // Toggle expanded view
  const toggleStoryExpansion = (storyId: string) => {
    setExpandedStories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(storyId)) {
        newSet.delete(storyId);
      } else {
        newSet.add(storyId);
      }
      return newSet;
    });
  };

  // Toggle epic expansion
  const toggleEpicExpansion = (epicId: string) => {
    setExpandedEpics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(epicId)) {
        newSet.delete(epicId);
      } else {
        newSet.add(epicId);
      }
      return newSet;
    });
  };

  // Start editing a story
  const handleStartEditing = (story: UserStory) => {
    setEditingStories(prev => new Set(prev).add(story.id));
    setEditedFields(prev => ({
      ...prev,
      [story.id]: {
        title: story.title,
        role: story.role,
        goal: story.goal,
        benefit: story.benefit,
        acceptanceCriteria: story.acceptanceCriteria || []
      }
    }));
    // Automatically expand the story when starting to edit
    setExpandedStories(prev => new Set(prev).add(story.id));
  };

  // Cancel editing
  const handleCancelEditing = (storyId: string) => {
    setEditingStories(prev => {
      const newSet = new Set(prev);
      newSet.delete(storyId);
      return newSet;
    });
    setEditedFields(prev => {
      const newFields = { ...prev };
      delete newFields[storyId];
      return newFields;
    });
  };

  // Update edited field value
  const handleFieldChange = (storyId: string, field: keyof UserStory, value: any) => {
    setEditedFields(prev => ({
      ...prev,
      [storyId]: {
        ...prev[storyId],
        [field]: value
      }
    }));
  };

  // Handle acceptance criteria change
  const handleAcceptanceCriteriaChange = (storyId: string, criteria: AcceptanceCriterion[]) => {
    setEditedFields(prev => ({
      ...prev,
      [storyId]: {
        ...prev[storyId],
        acceptanceCriteria: criteria
      }
    }));
  };

  // Save edited fields
  const handleSaveEdits = async (story: UserStory) => {
    const edits = editedFields[story.id];
    if (!edits) return;

    setUpdatingStatusStories(prev => new Set(prev).add(story.id));
    
    try {
      const result = await skillShopService.updateUserStoryFields(
        story.id, 
        edits,
        repoContext.repoUrl,
        repoContext.repoPath
      );
      
      if (result.success) {
        console.log('Field updates completed:', result);
        
        // Clear editing state
        handleCancelEditing(story.id);
        
        // Reload stories
        await loadStories();
      } else {
        console.error('Failed to update user story:', result.error);
        toast({
          title: 'Failed to update story',
          description: result.error || 'The story could not be updated.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error updating user story:', error);
      toast({
        title: 'Failed to update story',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setUpdatingStatusStories(prev => {
        const newSet = new Set(prev);
        newSet.delete(story.id);
        return newSet;
      });
    }
  };

  const handleRequestDeleteStory = (story: UserStory) => {
    setStoryToDelete(story);
    setShowDeleteStoryDialog(true);
  };

  const confirmDeleteStory = async () => {
    if (!storyToDelete) return;

    const id = storyToDelete.id;

    try {
      const result = await skillShopService.deleteUserStory(
        id,
        repoContext.repoUrl,
        repoContext.repoPath
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete user story');
      }

      setExpandedStories((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setEditingStories((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setEditedFields((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setGroomingStories((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

      setShowDeleteStoryDialog(false);
      setStoryToDelete(null);

      await loadStories();

      toast({
        title: 'User story deleted',
        description: `"${id}" was removed from the repository.`,
      });
    } catch (error) {
      console.error('Error deleting user story:', error);
      toast({
        title: 'Failed to delete user story',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Start editing an epic
  const handleStartEditingEpic = (epic: Epic) => {
    setEditingEpics(prev => new Set(prev).add(epic.id));
    setEditedEpicFields(prev => ({
      ...prev,
      [epic.id]: {
        title: epic.title,
        description: epic.description,
        status: epic.status
      }
    }));
    // Automatically expand the epic when starting to edit
    setExpandedEpics(prev => new Set(prev).add(epic.id));
  };

  // Cancel editing an epic
  const handleCancelEditingEpic = (epicId: string) => {
    setEditingEpics(prev => {
      const newSet = new Set(prev);
      newSet.delete(epicId);
      return newSet;
    });
    setEditedEpicFields(prev => {
      const newFields = { ...prev };
      delete newFields[epicId];
      return newFields;
    });
  };

  // Update edited epic field value
  const handleEpicFieldChange = (epicId: string, field: keyof Epic, value: any) => {
    setEditedEpicFields(prev => ({
      ...prev,
      [epicId]: {
        ...prev[epicId],
        [field]: value
      }
    }));
  };

  // Save edited epic fields
  const handleSaveEpicEdits = async (epic: Epic) => {
    const edits = editedEpicFields[epic.id];
    if (!edits) return;

    setUpdatingEpics(prev => new Set(prev).add(epic.id));
    
    try {
      const result = await skillShopService.updateEpicFields(
        epic.id, 
        edits,
        repoContext.repoUrl,
        repoContext.repoPath
      );
      
      if (result.success) {
        console.log('Epic field updates completed:', result);
        
        // Clear editing state
        handleCancelEditingEpic(epic.id);
        
        // Reload stories (which also reloads epics)
        await loadStories();
        
        toast({
          title: 'Epic updated',
          description: `"${epic.id}" has been updated successfully.`,
        });
      } else {
        console.error('Failed to update epic:', result.error);
        toast({
          title: 'Failed to update epic',
          description: result.error || 'The epic could not be updated.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error updating epic:', error);
      toast({
        title: 'Failed to update epic',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setUpdatingEpics(prev => {
        const newSet = new Set(prev);
        newSet.delete(epic.id);
        return newSet;
      });
    }
  };

  const handleInputChange = (field: keyof UserStory, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFormAcceptanceCriteriaChange = (criteria: AcceptanceCriterion[]) => {
    setFormData(prev => ({
      ...prev,
      acceptanceCriteria: criteria
    }));
  };

  const handleFormLinksChange = (links: string[]) => {
    setFormData(prev => ({
      ...prev,
      links
    }));
  };

  const handleLinksChange = (storyId: string, links: string[]) => {
    setEditedFields(prev => ({
      ...prev,
      [storyId]: {
        ...prev[storyId],
        links
      }
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.role || !formData.goal || !formData.benefit) {
      toast({
        title: 'Missing required fields',
        description: 'Title, role, goal, and benefit are required.',
        variant: 'destructive',
      });
      return;
    }

    // Generate a new story ID (this should ideally be done on the server)
    const storyCount = stories.length + 1;
    const storyId = `STORY-${storyCount.toString().padStart(3, '0')}`;
    
    const newStory: UserStory = {
      id: storyId,
      title: formData.title as string,
      role: formData.role as string,
      goal: formData.goal as string,
      benefit: formData.benefit as string,
      acceptanceCriteria: formData.acceptanceCriteria || [],
      status: formData.status,
      section: formData.section,
      epicId: formData.epicId,
      links: formData.links || []
    };

    setStories(prev => [...prev, newStory]);
    
    // Reset form
    setFormData({
      title: '',
      role: '',
      goal: '',
      benefit: '',
      acceptanceCriteria: [],
      status: 'Not Started',
      section: '',
      epicId: '',
      links: []
    });
    setShowForm(false);
    
    console.log('New user story created:', newStory);
  };

  // Get unique sections for filtering
  const uniqueSections = Array.from(new Set(stories.map(s => s.section).filter(Boolean))) as string[];

  // Get unique epics for filtering
  const uniqueEpics = Array.from(new Set(stories.map(s => s.epicId).filter(Boolean))) as string[];

  // Filter stories based on search term and filters
  const filteredStories = stories.filter(story => {
    const matchesSearch = searchTerm === '' ||
      story.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      story.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      story.goal.toLowerCase().includes(searchTerm.toLowerCase()) ||
      story.benefit.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (story.acceptanceCriteria && story.acceptanceCriteria.some(ac => ac.text.toLowerCase().includes(searchTerm.toLowerCase())));

    const matchesStatus = statusFilter === 'all' || story.status === statusFilter;
    const matchesSection = sectionFilter === 'all' || story.section === sectionFilter;
    const matchesEpic = epicFilter === 'all' || story.epicId === epicFilter;

    return matchesSearch && matchesStatus && matchesSection && matchesEpic;
  });

  // Filter epics based on active filters
  const filteredEpics = epics.filter(epic => {
    const matchesStatus = statusFilter === 'all' || epic.status === statusFilter;
    
    // Check if any story in this epic matches the current filters
    const epicStories = filteredStories.filter(s => s.epicId === epic.id);
    const hasMatchingStories = epicStories.length > 0;
    
    // Show epic if:
    // 1. Status filter matches AND
    // 2. Either there are matching stories, OR no search/section/epic filters are active
    //    (this ensures empty epics are shown when no content filters are applied)
    const noContentFiltersActive = searchTerm === '' && sectionFilter === 'all' && epicFilter === 'all';
    
    return matchesStatus && (hasMatchingStories || noContentFiltersActive);
  });

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSectionFilter('all');
    setEpicFilter('all');
  };

  // Check if any filters are active
  const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all' || sectionFilter !== 'all' || epicFilter !== 'all';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Not Started': return '#6c757d';
      case 'In Progress': return '#007bff';
      case 'In Review': return '#8b5cf6';
      case 'Complete': return '#28a745';
      case 'Blocked': return '#dc3545';
      case 'On Hold': return '#ffc107';
      default: return '#6c757d';
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <FileText style={{ marginRight: '12px', color: '#0066cc' }} />
              <h2 style={{ margin: 0 }}>User Stories</h2>
            </div>
            <div style={{ height: '24px', width: '1px', backgroundColor: '#e5e7eb' }} />
            <ActiveProductIndicator />
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '4px', backgroundColor: '#f0f0f0', borderRadius: '6px', padding: '4px' }}>
              <button
                onClick={() => setViewMode('epics')}
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: viewMode === 'epics' ? '#0066cc' : 'transparent',
                  color: viewMode === 'epics' ? 'white' : '#666',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
              >
                Epics
              </button>
              <button
                onClick={() => setViewMode('stories')}
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: viewMode === 'stories' ? '#0066cc' : 'transparent',
                  color: viewMode === 'stories' ? 'white' : '#666',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
              >
                Stories
              </button>
            </div>
            <button
              className="btn-compact"
              onClick={() => setShowForm(!showForm)}
            >
              <Plus size={14} />
              {showForm ? 'Cancel' : 'New Story'}
            </button>

            {/* Import Button with Dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                className="btn-compact"
                onClick={() => {
                  setShowImportMenu(!showImportMenu);
                  setShowExportMenu(false);
                }}
                style={{ backgroundColor: '#059669' }}
              >
                <Download size={14} />
                Import
              </button>
              {showImportMenu && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                    zIndex: 50,
                    minWidth: '160px'
                  }}
                >
                  <button
                    onClick={() => {
                      setShowJiraImport(true);
                      setShowImportMenu(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 16px',
                      textAlign: 'left',
                      border: 'none',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      fontSize: '14px',
                      borderRadius: '8px 8px 0 0'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    From JIRA
                  </button>
                  <button
                    onClick={() => {
                      setShowLinearImport(true);
                      setShowImportMenu(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 16px',
                      textAlign: 'left',
                      border: 'none',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    From Linear
                  </button>
                  <button
                    onClick={() => {
                      setShowCsvImport(true);
                      setShowImportMenu(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 16px',
                      textAlign: 'left',
                      border: 'none',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      fontSize: '14px',
                      borderRadius: '0 0 8px 8px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    From CSV
                  </button>
                </div>
              )}
            </div>

            {/* Export Button with Dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                className="btn-compact"
                onClick={() => {
                  setShowExportMenu(!showExportMenu);
                  setShowImportMenu(false);
                }}
                disabled={epics.length === 0 && stories.length === 0}
                style={{ backgroundColor: '#7c3aed' }}
              >
                <Upload size={14} />
                Export
              </button>
              {showExportMenu && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                    zIndex: 50,
                    minWidth: '160px'
                  }}
                >
                  <button
                    onClick={() => {
                      setShowJiraExport(true);
                      setShowExportMenu(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 16px',
                      textAlign: 'left',
                      border: 'none',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      fontSize: '14px',
                      borderRadius: '8px 8px 0 0'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    To JIRA
                  </button>
                  <button
                    onClick={() => {
                      setShowLinearExport(true);
                      setShowExportMenu(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 16px',
                      textAlign: 'left',
                      border: 'none',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    To Linear
                  </button>
                  <button
                    onClick={() => {
                      setShowPdfExport(true);
                      setShowExportMenu(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 16px',
                      textAlign: 'left',
                      border: 'none',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    To PDF
                  </button>
                  <button
                    onClick={() => {
                      setShowDiffReport(true);
                      setShowExportMenu(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 16px',
                      textAlign: 'left',
                      border: 'none',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      fontSize: '14px',
                      borderRadius: '0 0 8px 8px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    Diff Report
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {repoContext.repoUrl && (
          <div style={{ marginBottom: '16px', fontSize: '12px', color: '#2563eb' }}>
            Managing stories for <span style={{ fontFamily: 'monospace' }}>{repoContext.repoUrl}</span>
            {repoContext.repoPath && (
              <span style={{ color: '#475569' }}> ({repoContext.repoPath})</span>
            )}
          </div>
        )}

        <RequestInput
          title="Quick User Story Request"
          description="Describe your user story request in natural language."
          examples={[
            "Create a user story for login functionality",
            "Update story US-001 to completed",
            "Show me all user stories for the dashboard",
            "Create a story for password reset feature"
          ]}
          value={requestProcessor.request}
          isProcessing={requestProcessor.isProcessing}
          isListening={requestProcessor.isListening}
          lastResponse={requestProcessor.lastResponse}
          speechSupported={requestProcessor.speechSupported}
          onValueChange={requestProcessor.setRequest}
          onProcess={requestProcessor.handleProcess}
          onVoiceInput={requestProcessor.toggleListening}
          onKeyPress={requestProcessor.handleKeyPress}
          data-walkthrough="quick-request"
        />

        {showForm && (
          <UserStoryForm
            formData={formData}
            statusOptions={statusOptions}
            releases={releases}
            onInputChange={handleInputChange}
            onAcceptanceCriteriaChange={handleFormAcceptanceCriteriaChange}
            onLinksChange={handleFormLinksChange}
            onSubmit={handleSubmit}
            onCancel={() => setShowForm(false)}
          />
        )}

        <div>
          {loadError && (
            <div
              style={{
                marginBottom: '16px',
                padding: '12px 16px',
                borderRadius: '8px',
                backgroundColor: '#fee2e2',
                color: '#b91c1c',
                border: '1px solid #fecaca'
              }}
              role="alert"
            >
              {loadError}
            </div>
          )}

          <UserStoryListHeader
            filteredCount={filteredStories.length}
            totalCount={stories.length}
            isLoading={loadingStories}
            onRefresh={loadStories}
          />

          <UserStoryFilters
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            sectionFilter={sectionFilter}
            epicFilter={epicFilter}
            onSearchChange={setSearchTerm}
            onStatusFilterChange={setStatusFilter}
            onSectionFilterChange={setSectionFilter}
            onEpicFilterChange={setEpicFilter}
            onClearFilters={handleClearFilters}
            hasActiveFilters={hasActiveFilters}
            statusOptions={statusOptions}
            sections={uniqueSections}
            epics={uniqueEpics}
          />

          {loadingStories ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <Loader size={48} style={{ marginBottom: '16px', opacity: 0.5, animation: 'spin 1s linear infinite' }} />
              <p>Loading user stories from markdown files...</p>
            </div>
          ) : viewMode === 'epics' ? (
            // Epic view
            epics.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <FileText size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <p>No user stories yet{repoContext.repoUrl ? ' for this product' : ''}.</p>
                <p style={{ fontSize: '14px', marginTop: '8px', color: '#999' }}>
                  Create your first user story to get started, or import stories from an external system.
                </p>
              </div>
            ) : filteredEpics.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <Search size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <p>No epics match your filter criteria.</p>
                <button
                  onClick={handleClearFilters}
                  className="btn"
                  style={{
                    marginTop: '16px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <FilterX size={16} />
                  Clear All Filters
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {filteredEpics.map((epic) => {
                  const epicStories = filteredStories.filter(s => s.epicId === epic.id);
                  return (
                    <div key={epic.id}>
                      <EpicCard
                        epic={epic}
                        isExpanded={expandedEpics.has(epic.id)}
                        storyCount={epicStories.length}
                        isGrooming={groomingEpics.has(epic.id)}
                        isEditing={editingEpics.has(epic.id)}
                        isUpdating={updatingEpics.has(epic.id)}
                        editedFields={editedEpicFields[epic.id] || {}}
                        statusOptions={statusOptions}
                        onToggleExpansion={() => toggleEpicExpansion(epic.id)}
                        onGroomEpic={() => handleGroomEpic(epic)}
                        onStartEditing={() => handleStartEditingEpic(epic)}
                        onCancelEditing={() => handleCancelEditingEpic(epic.id)}
                        onSaveEdits={() => handleSaveEpicEdits(epic)}
                        onFieldChange={(field, value) => handleEpicFieldChange(epic.id, field, value)}
                        getStatusColor={getStatusColor}
                      />
                      {expandedEpics.has(epic.id) && (
                        <div style={{ marginLeft: '40px', marginTop: '12px', marginBottom: '24px' }}>
                          {epicStories.length === 0 ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                              No stories in this epic
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                              {epicStories.map((story, index) => (
                                <UserStoryCard
                                  key={index}
                                  story={story}
                                  isExpanded={expandedStories.has(story.id)}
                                  isGrooming={groomingStories.has(story.id)}
                                  isEditing={editingStories.has(story.id)}
                                  isUpdating={updatingStatusStories.has(story.id)}
                                  editedFields={editedFields[story.id] || {}}
                                  statusOptions={statusOptions}
                                  releases={releases}
                                  getStatusColor={getStatusColor}
                                  onToggleExpansion={() => toggleStoryExpansion(story.id)}
                                  onGroomStory={() => handleGroomStory(story)}
                                  onStartEditing={() => handleStartEditing(story)}
                                  onCancelEditing={() => handleCancelEditing(story.id)}
                                  onSaveEdits={() => handleSaveEdits(story)}
                                  onFieldChange={(field, value) => handleFieldChange(story.id, field, value)}
                                  onStatusUpdate={(newStatus) => handleStatusUpdate(story, newStatus)}
                                  onAcceptanceCriteriaChange={(criteria) => handleAcceptanceCriteriaChange(story.id, criteria)}
                                  onLinksChange={(links) => handleLinksChange(story.id, links)}
                                  onRequestDelete={() => handleRequestDeleteStory(story)}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            // Stories view
            stories.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <FileText size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <p>No user stories yet{repoContext.repoUrl ? ' for this product' : ''}.</p>
                <p style={{ fontSize: '14px', marginTop: '8px', color: '#999' }}>
                  Create your first user story to get started, or import stories from an external system.
                </p>
              </div>
            ) : filteredStories.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <Search size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <p>No user stories match your search and filter criteria.</p>
                <button
                  onClick={handleClearFilters}
                  className="btn"
                  style={{
                    marginTop: '16px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <FilterX size={16} />
                  Clear All Filters
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {filteredStories.map((story, index) => (
                  <UserStoryCard
                    key={index}
                    story={story}
                    isExpanded={expandedStories.has(story.id)}
                    isGrooming={groomingStories.has(story.id)}
                    isEditing={editingStories.has(story.id)}
                    isUpdating={updatingStatusStories.has(story.id)}
                    editedFields={editedFields[story.id] || {}}
                    statusOptions={statusOptions}
                    releases={releases}
                    getStatusColor={getStatusColor}
                    onToggleExpansion={() => toggleStoryExpansion(story.id)}
                    onGroomStory={() => handleGroomStory(story)}
                    onStartEditing={() => handleStartEditing(story)}
                    onCancelEditing={() => handleCancelEditing(story.id)}
                    onSaveEdits={() => handleSaveEdits(story)}
                    onFieldChange={(field, value) => handleFieldChange(story.id, field, value)}
                    onStatusUpdate={(newStatus) => handleStatusUpdate(story, newStatus)}
                    onAcceptanceCriteriaChange={(criteria) => handleAcceptanceCriteriaChange(story.id, criteria)}
                    onLinksChange={(links) => handleLinksChange(story.id, links)}
                    onRequestDelete={() => handleRequestDeleteStory(story)}
                  />
                ))}
              </div>
            )
          )}
        </div>
      </div>

      <AlertDialog
        open={showDeleteStoryDialog}
        onOpenChange={(open) => {
          setShowDeleteStoryDialog(open);
          if (!open) setStoryToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User Story</AlertDialogTitle>
            <AlertDialogDescription>
              {storyToDelete
                ? `Are you sure you want to delete ${storyToDelete.id}: ${storyToDelete.title}? This removes it from user story markdown.  You can revert in Changes to undo.`
                : 'Are you sure you want to delete this user story? You can revert in Changes to undo.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteStory} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add CSS for the loader animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Import/Export Dialogs */}
      {showJiraImport && (
        <JiraUserStoryImportDialog
          onClose={() => setShowJiraImport(false)}
          onImportComplete={() => {
            loadStories();
            toast({
              title: 'Import Complete',
              description: 'User stories have been imported from JIRA.',
            });
          }}
        />
      )}

      {showLinearImport && (
        <LinearUserStoryImportDialog
          onClose={() => setShowLinearImport(false)}
          onImportComplete={() => {
            loadStories();
            toast({
              title: 'Import Complete',
              description: 'User stories have been imported from Linear.',
            });
          }}
        />
      )}

      {showCsvImport && (
        <CsvUserStoryImportDialog
          onClose={() => setShowCsvImport(false)}
          onImportComplete={() => {
            loadStories();
            toast({
              title: 'Import Complete',
              description: 'User stories have been imported from CSV.',
            });
          }}
        />
      )}

      {showJiraExport && (
        <JiraUserStoryExportDialog
          epics={epics}
          stories={stories}
          onClose={() => setShowJiraExport(false)}
          onExportComplete={() => {
            toast({
              title: 'Export Complete',
              description: 'User stories have been exported to JIRA.',
            });
          }}
        />
      )}

      {showLinearExport && (
        <LinearUserStoryExportDialog
          epics={epics}
          stories={stories}
          onClose={() => setShowLinearExport(false)}
          onExportComplete={() => {
            toast({
              title: 'Export Complete',
              description: 'User stories have been exported to Linear.',
            });
          }}
        />
      )}

      {showPdfExport && (
        <PdfUserStoryExportDialog
          epics={epics}
          stories={stories}
          onClose={() => setShowPdfExport(false)}
          onExportComplete={() => {
            toast({
              title: 'Export Complete',
              description: 'User stories have been exported to PDF.',
            });
          }}
        />
      )}

      {showDiffReport && (
        <DiffReportDialog
          onClose={() => setShowDiffReport(false)}
          onExportComplete={() => {
            toast({
              title: 'Diff Report Generated',
              description: 'Your diff report PDF has been downloaded.',
            });
          }}
        />
      )}
    </div>
  );
}
